import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  api,
  type Job,
  type Site,
  type SiteContentHead,
  type SiteVersionContent,
  type SiteVersionOperation,
  type SiteVersionSummary,
} from "../../../lib/api";
import type { EditorObject } from "@prototypical/editor-contract";
import { imageEditSource } from "../imageSource";
import { authoredStyles, type EditorManifest } from "../preview-bridge/manifestV3";
import { buildSourceEditDocument, savedSourceStyles } from "../preview-bridge/sourceEdits";
import { useAppDialog } from "../../../components/AppDialog";
import { forgePreviewUrl, previewFrameUrl } from "../../../lib/domains";
import { friendlyError } from "../../../lib/friendlyError";
import {
  DEFAULT_STYLES,
  PALETTE,
  contrastRatio,
  normalizeManifest,
  pageFile,
  pageHref,
  postToPreview,
  samePage,
  toHexColor,
  waitForAppliedBuild,
  type AssistantEntry,
  type Busy,
  type EditConflict,
  type FieldAttributes,
  type ImageUploadState,
  type InspectorTab,
  type Mode,
  type PageHint,
  type PreviewMessage,
  type RailTab,
  type StyleState,
} from "../preview-bridge";
import { pageLabel } from "../preview-bridge/messaging";
import { ASSISTANT_ENABLED } from "../assistantAvailability";
import { isVersionConflict, savedPreviewFailure } from "../saveFeedback";

function hasPendingV2Preview(head: SiteContentHead) {
  return head.content.schemaVersion === 2 && !head.buildId && !!head.content.operations?.length;
}

export function useSiteEditor() {

  const { siteId } = useParams<{ siteId: string }>();
  const dialog = useAppDialog();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const prepareAttempted = useRef(false);
  /**
   * The request the assistant asked a clarifying question about, held until the
   * owner answers. A ref rather than state because nothing renders from it —
   * it only rides along on the next request.
   */
  const pendingQuestion = useRef<{ instruction: string; question: string } | null>(null);
  const handshakeTimer = useRef<number | null>(null);
  const expectedHandshake = useRef<string | null>(null);
  const pageSelectionInitialized = useRef(false);
  const styleUndo = useRef<StyleState[]>([]);
  const styleRedo = useRef<StyleState[]>([]);
  const changedFields = useRef(new Set<string>());
  const changedStyles = useRef(new Set<string>());
  const uploadAbort = useRef<AbortController | null>(null);
  const reloadGeneration = useRef(0);
  const conflictResolution = useRef(0);
  const [site, setSite] = useState<Site | null>(null);
  const [head, setHead] = useState<SiteContentHead | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [manifest, setManifest] = useState<EditorManifest | null>(null);
  const manifestRef = useRef(manifest);
  manifestRef.current = manifest;
  const [versions, setVersions] = useState<SiteVersionSummary[]>([]);
  const [builds, setBuilds] = useState<Job[]>([]);
  const [buildId, setBuildId] = useState<string | null>(null);
  const [preparedSource, setPreparedSource] = useState<{ previewBuildId: string; sourceBuildId?: string; sourceBundleHash?: string } | null>(null);
  const [ready, setReady] = useState(false);
  const [frameTimedOut, setFrameTimedOut] = useState(false);
  const [frameKey, setFrameKey] = useState(0);
  const [mode, setMode] = useState<Mode>("direct");
  const [railTab, setRailTab] = useState<RailTab>("pages");
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("inspect");
  const [currentPage, setCurrentPage] = useState("index.html");
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [attributes, setAttributes] = useState<FieldAttributes>({});
  const [imageAssetIds, setImageAssetIds] = useState<Record<string, string>>({});
  const [imageUpload, setImageUpload] = useState<ImageUploadState | null>(null);
  const [sectionEdits, setSectionEdits] = useState<Record<string, { visible?: boolean; order?: number }>>({});
  const [deletedElements, setDeletedElements] = useState<Array<Extract<SiteVersionOperation, { op: "delete-element" }>>>([]);
  const [styles, setStyles] = useState<StyleState>(DEFAULT_STYLES);
  const [recentStyles, setRecentStyles] = useState<string[]>([]);
  const [assistantMessage, setAssistantMessage] = useState("");
  const [assistantLog, setAssistantLog] = useState<AssistantEntry[]>([
    { id: "welcome", role: "assistant", text: "Tell me what should change. Your current selection stays attached as context." },
  ]);
  const [activeJob, setActiveJob] = useState<string | null>(null);
  const [motionPaused, setMotionPaused] = useState(false);
  const [advancedMotion, setAdvancedMotion] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [dirty, setDirty] = useState(false);
  const [saveProgress, setSaveProgress] = useState<string | null>(null);
  const [conflict, setConflict] = useState<EditConflict | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pageHint, setPageHint] = useState<PageHint | null>(null);

  const refreshVersions = useCallback(async () => {
    if (!siteId) return;
    const history = await api.listSiteVersions(siteId);
    setVersions(history.versions);
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    const versionHistory = api.listSiteVersions(siteId).catch(() => ({
      siteId,
      currentVersion: 0,
      count: 0,
      versions: [],
    }));
    const buildHistory = api.listBuilds(siteId).catch(() => {
      if (!cancelled) setNotice("Build history is temporarily unavailable. Editing still works.");
      return { jobs: [], previewBuildId: null, liveBuildId: null, pendingBuildId: null };
    });
    Promise.all([api.getSite(siteId), api.getSiteContent(siteId), versionHistory, buildHistory])
      .then(([siteRow, content, history, buildHistory]) => {
        if (cancelled) return;
        if (hasPendingV2Preview(content)) {
          setError(`Server draft ${content.version} is saved, but its preview is not ready. Refresh after the preview finishes to reopen the latest draft.`);
          return;
        }
        setSite(siteRow);
        setHead(content);
        setBuildId(siteRow.previewBuildId ?? siteRow.liveBuildId);
        setVersions(history.versions);
        setBuilds(buildHistory.jobs);
        const savedValues = Object.fromEntries(Object.entries(content.content.fields).filter(([, field]) => field.type !== "color").map(([key, field]) => [key, field.value]));
        if (content.content.schemaVersion === 2) {
          // Older applied bundles updated the HTML and field.value without
          // updating manifest values/metadata. The saved operations retain the
          // authoritative text and attributes for those existing drafts.
          const savedAttributes: FieldAttributes = {};
          for (const operation of content.content.operations ?? []) {
            if (operation.op === "set-field") savedValues[operation.path] = operation.value;
            if (operation.op === "set-link") {
              if (operation.text !== undefined) savedValues[operation.path] = operation.text;
              savedAttributes[operation.path] = { ...savedAttributes[operation.path], href: operation.href };
            }
            if (operation.op === "set-image") {
              // Resolve the display URL from the rendered bundle, not a saved
              // upload ID or an external source that apply may have localized.
              delete savedValues[operation.path];
              savedAttributes[operation.path] = {
                ...savedAttributes[operation.path],
                ...(operation.alt !== undefined ? { alt: operation.alt } : {}),
                ...(operation.focalX !== undefined ? { focalX: operation.focalX } : {}),
                ...(operation.focalY !== undefined ? { focalY: operation.focalY } : {}),
              };
            }
          }
          setAttributes(savedAttributes);
        }
        setValues(savedValues);
        if (content.content.schemaVersion !== 1) {
          setImageAssetIds(Object.fromEntries((content.content.operations ?? []).flatMap((operation) =>
            operation.op === "set-image" && operation.assetId ? [[operation.path, operation.assetId]] : [])));
        }
        const savedStyles = Object.fromEntries(Object.entries(content.content.fields).filter(([key]) => key.startsWith("style.")).map(([key, field]) => [key.slice(6), field.value]));
        const savedColors = Object.fromEntries(Object.entries(content.content.fields).filter(([key, field]) => key.startsWith("theme.") && field.type === "color").map(([key, field]) => {
          const token = key.slice(6);
          return [token === "background" ? "bg" : token === "text" ? "fg" : token, field.value];
        }));
        setStyles({ ...DEFAULT_STYLES, ...savedStyles, ...savedColors });
        if (content.content.schemaVersion === 3) {
          for (const operation of content.content.operations) {
            if (operation.op === "set-token") setStyles(current => ({ ...current, [operation.name]: operation.value }));
            if (operation.op === "set-font") setStyles(current => ({ ...current, [`font.${operation.id}`]: operation.cssFamily }));
            if (operation.op === "set-field") {
              if (operation.capability === "href" || operation.capability === "alt") {
                const capability = operation.capability;
                setAttributes(current => ({ ...current, [operation.path]: { ...current[operation.path], [capability]: operation.value } }));
              } else setValues(current => ({ ...current, [operation.path]: operation.value }));
            }
          }
        }
      })
      .catch((reason) => {
        if (!cancelled) setError(friendlyError(reason, "This site could not be opened for editing. Refresh to try again."));
      });
    return () => { cancelled = true; };
  }, [siteId, reloadKey]);

  useEffect(() => {
    if (!siteId || site?.status !== "building") return;
    let stopped = false;
    const timer = window.setInterval(async () => {
      try {
        const next = await api.getSite(siteId);
        if (stopped) return;
        setSite(next);
        if (next.previewBuildId && next.status !== "building") {
          setBuildId(next.previewBuildId);
          setReady(false);
          setFrameTimedOut(false);
          prepareAttempted.current = false;
          setFrameKey((value) => value + 1);
        }
      } catch {
        // Reconciliation is best-effort; retry while the row says building.
      }
    }, 3000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [site?.status, siteId]);

  const model = useMemo(() => normalizeManifest(manifest, values), [manifest, values]);
  const modelPagesRef = useRef(model.pages);
  modelPagesRef.current = model.pages;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  // The postMessage listener is installed once, so it needs a live view of the
  // field values to diff an inline edit against.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const buildIdRef = useRef(buildId);
  buildIdRef.current = buildId;

  // Resolve an in-preview link (e.g. the site's own nav) to a manifest page.
  // The preview proxy rewrites every internal href to /api/preview-frame/<id>/…
  // so strip that prefix before matching — otherwise nav links never resolve
  // and Ctrl/Cmd+click navigation is silently ignored.
  const pageForHref = (href: string) => {
    const unproxied = href.replace(/^\/api\/preview-frame\/[^/]+/, "") || "/";
    const clean = unproxied.split(/[?#]/)[0] || "/";
    return modelPagesRef.current.find((page) =>
      samePage(page.path, clean) || samePage(page.file, clean) || samePage(page.route, clean));
  };
  const isCurrentPage = (page: { path: string; file?: string; route?: string }) =>
    samePage(page.path, currentPageRef.current) ||
    samePage(page.file, currentPageRef.current) ||
    samePage(page.route, currentPageRef.current);
  const goToPage = (path: string) => {
    setSelectedPath(null);
    setCurrentPage(path);
    setReady(false);
    setFrameKey((value) => value + 1);
  };
  useEffect(() => {
    if (!manifest || !model.pages.length) return;
    const currentIsValid = model.pages.some((page) =>
      samePage(page.path, currentPage) || samePage(page.file, currentPage) || samePage(page.route, currentPage));
    if (!pageSelectionInitialized.current) {
      pageSelectionInitialized.current = true;
      const home = model.pages.find((page) =>
        page.route === "/" || samePage(page.file, "index.html") || samePage(page.path, "index.html"));
      setCurrentPage((home ?? model.pages[0]).path);
    } else if (!currentIsValid) {
      setCurrentPage(model.pages[0].path);
    }
  }, [currentPage, manifest, model.pages]);

  // Resolve metadata before navigating: canceling a provisional proxy redirect
  // while switching to the canonical URL can leave the iframe blank.
  const frameBase = buildId && manifest ? manifest.schemaVersion === 3 ? `${forgePreviewUrl(buildId)}/` : previewFrameUrl(buildId) : null;
  const previewBase = buildId ? forgePreviewUrl(buildId) : null;
  const currentPageModel = model.pages.find((page) => samePage(page.path, currentPage) || samePage(page.file, currentPage) || samePage(page.route, currentPage));
  const frameRoute = (currentPageModel?.route ?? pageHref(currentPage)).replace(/^\//, "");
  const previewUrl = frameBase
    ? new URL(frameRoute || ".", new URL(frameBase, window.location.origin)).toString()
    : null;
  const publicPreviewUrl = previewBase
    ? new URL(frameRoute || ".", `${previewBase}/`).toString()
    : null;
  const previewOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const selectedField = model.fields.find((field) => field.path === selectedPath);
  const selectedObject = model.objects.flatMap(function flatten(object): EditorObject[] {
    return [object, ...(object.children ?? []).flatMap(flatten)];
  }).find((object) => object.fieldPath === selectedPath || object.id === selectedPath);
  const currentPageRoot = model.objects.find((object) =>
    samePage(object.page, currentPage) ||
    (object.kind === "page" && object.id === `page:${currentPageModel?.id}`),
  );
  const isDeleted = (object: EditorObject) => deletedElements.some((operation) =>
    operation.target === "field"
      ? object.fieldPath === operation.id
      : object.id === operation.id && (operation.target !== "section" || object.kind === "section"));
  const pruneDeleted = (objects: EditorObject[]): EditorObject[] => objects
    .filter((object) => !isDeleted(object))
    .map((object) => ({ ...object, children: pruneDeleted(object.children ?? []) }));
  const pageObjects = pruneDeleted(currentPageRoot?.children ?? []);

  // Fast path: the built bundle already carries editor-manifest.json, served
  // same-origin via the preview proxy. Loading it immediately fills the Pages
  // rail and the Ctrl/Cmd+click page lookup in well under a second, instead of
  // showing a lone "Home" until prepareEditor finishes instrumenting (which
  // can take tens of seconds on a fresh open). prepare() stays authoritative —
  // its manifest replaces this one when it lands.
  useEffect(() => {
    if (!buildId || manifest) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${previewFrameUrl(buildId)}editor-manifest.json`);
        if (!response.ok) return;
        const parsed = await response.json() as EditorManifest;
        if (cancelled || (parsed?.schemaVersion !== 2 && parsed?.schemaVersion !== 3) || !Array.isArray(parsed.pages)) return;
        setManifest((current) => current ?? parsed);
      } catch {
        // Bundle without a stored manifest — prepare() covers it.
      }
    })();
    return () => { cancelled = true; };
  }, [buildId, manifest]);

  const prepare = useCallback(async () => {
    if (!siteId || busy === "prepare") return;
    const generation = reloadGeneration.current;
    setBusy("prepare");
    setError(null);
    try {
      const result = await api.prepareEditor(siteId);
      if (generation !== reloadGeneration.current) return;
      setPreparedSource({ previewBuildId: result.buildId, sourceBuildId: result.sourceBuildId, sourceBundleHash: result.sourceBundleHash });
      prepareAttempted.current = true;
      setManifest(result.manifest);
      // Reload the canvas only when prepare actually moved us to a different
      // bundle. Shipped builds already carry their manifest, so prepare is a
      // no-op there — remounting the iframe anyway restarted the handshake
      // mid-flight and read as the editor failing to connect.
      if (buildIdRef.current !== result.buildId) {
        setBuildId(result.buildId);
        setReady(false);
        setFrameKey((value) => value + 1);
      }
      const refreshedSite = await api.getSite(siteId).catch(() => null);
      if (refreshedSite && generation === reloadGeneration.current) setSite(refreshedSite);
    } catch (reason: any) {
      if (generation === reloadGeneration.current) setError(friendlyError(reason, "This site could not be made editable. Build a preview first, then reopen the editor."));
    } finally {
      if (generation === reloadGeneration.current) setBusy(null);
    }
  }, [busy, siteId]);

  useEffect(() => {
    const onMessage = (event: MessageEvent<PreviewMessage>) => {
      // The preview iframe is sandboxed without allow-same-origin, so its origin
      // is opaque ("null") and can't be matched against previewOrigin. Authenticate
      // by frame identity instead: the message must come from our own iframe's
      // window and carry the bridge's source tag. event.source can't be forged, so
      // this is strictly stronger than an origin-string compare.
      if (event.source !== frameRef.current?.contentWindow || event.data?.source !== "protopal-preview") return;
      if (event.data.type === "ready") {
        if (!event.data.requestId || event.data.requestId !== expectedHandshake.current) return;
        const reportedPage = modelPagesRef.current.find((page) => page.id === event.data.page);
        if (reportedPage) {
          setCurrentPage((current) => samePage(current, reportedPage.path) ? current : reportedPage.path);
        }
        setReady(true);
        setFrameTimedOut(false);
        setValues((current) => {
          const incoming = Object.fromEntries((event.data.fields ?? []).map((field) => [field.path, field.value]));
          const local = Object.fromEntries([...changedFields.current].map((path) => [path, current[path] ?? ""]));
          return { ...current, ...incoming, ...local };
        });
        setStyles((current) => {
          if (manifestRef.current?.schemaVersion === 3) return current;
          const colors = event.data.colors ?? {};
          return {
            ...current,
            bg: toHexColor(colors.bg ?? colors.background, current.bg),
            fg: toHexColor(colors.fg ?? colors.text, current.fg),
            muted: toHexColor(colors.muted, current.muted),
            primary: toHexColor(colors.primary, current.primary),
            accent: toHexColor(colors.accent, current.accent),
          };
        });
      }
      if (event.data.type === "select" && event.data.path) {
        setSelectedPath(event.data.path);
        setValues((current) => ({ ...current, [event.data.path!]: event.data.value ?? current[event.data.path!] ?? "" }));
        setInspectorTab("inspect");
        setRailTab("objects");
        if (event.data.href) {
          const page = pageForHref(event.data.href);
          if (page && !isCurrentPage(page)) setPageHint({ label: page.label || pageLabel(page.path) });
        }
      }
      if (event.data.type === "blocked-navigation") {
        setNotice(event.data.kind === "phone"
          ? "Phone links work on your published site. Here they stay put so the editor doesn't lose your place."
          : event.data.kind === "email"
            ? "Email links work on your published site. Here they stay put so the editor doesn't lose your place."
            : "External links work on your published site. Here they stay put so the editor doesn't lose your place.");
      }
      if ((event.data.type === "navigate" || event.data.type === "navigate-hint") && event.data.href) {
        const page = pageForHref(event.data.href);
        if (page && !isCurrentPage(page)) {
          if (event.data.type === "navigate") goToPage(page.path);
          else setPageHint({ label: page.label || pageLabel(page.path) });
        }
      }
      if ((event.data.type === "inline-edit-draft" || event.data.type === "inline-edit-commit") && event.data.path) {
        const path = event.data.path;
        const next = event.data.value ?? "";
        // Only a real change marks the document dirty. The bridge now diffs
        // before committing, but the parent refuses to take its word for it:
        // a phantom dirty flag is what put an "unsaved changes" prompt in
        // front of every assistant message after a bare double-click.
        if (valuesRef.current[path] === next) return;
        setValues((current) => ({ ...current, [path]: next }));
        changedFields.current.add(path);
        setDirty(true);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [previewOrigin]);

  useEffect(() => {
    if (!manifest) return;
    const defaults = { ...authoredStyles(manifest), ...(!head?.buildId ? savedSourceStyles(head?.content) : {}) };
    setStyles(current => ({ ...current, ...Object.fromEntries(Object.entries(defaults).filter(([token]) => !changedStyles.current.has(token))) }));
    setValues((current) => ({
      ...Object.fromEntries(manifest.fields.map((field) => [
        field.path,
        field.type === "image"
          ? manifest.schemaVersion === 2 ? field.value : field.values?.src ?? field.value
          : field.values?.text ?? field.value,
      ])),
      ...current,
    }));
    setAttributes((current) => {
      const next = { ...current };
      for (const field of manifest.fields) {
        next[field.path] = {
          href: field.values?.href ?? field.metadata?.href,
          alt: field.values?.alt ?? field.metadata?.alt,
          focalX: field.metadata?.focalX,
          focalY: field.metadata?.focalY,
          ...current[field.path],
        };
      }
      return next;
    });
  }, [manifest]);

  useEffect(() => {
    if (!ready || !previewOrigin) return;
    postToPreview(frameRef.current, previewOrigin, {
      type: "set-mode",
      mode: mode === "assistant" ? "ai" : "tactile",
    });
  }, [mode, previewOrigin, ready]);

  useEffect(() => {
    if (!ready || !previewOrigin || !selectedPath) return;
    postToPreview(frameRef.current, previewOrigin, { type: "set-selection", paths: [selectedPath] });
  }, [previewOrigin, ready, selectedPath]);

  useEffect(() => {
    setFrameTimedOut(false);
    if (!previewUrl || ready) return;
    const timeout = window.setTimeout(() => setFrameTimedOut(true), 10_000);
    return () => window.clearTimeout(timeout);
  }, [buildId, frameKey, previewUrl, ready]);

  useEffect(() => {
    if (!previewBase || ready || prepareAttempted.current || busy === "prepare") return;
    const timeout = window.setTimeout(() => {
      prepareAttempted.current = true;
      void prepare();
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [busy, prepare, previewBase, ready]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(null), 4200);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    if (!pageHint) return;
    const timeout = window.setTimeout(() => setPageHint(null), 6000);
    return () => window.clearTimeout(timeout);
  }, [pageHint]);

  useEffect(() => { setPageHint(null); }, [currentPage]);

  useEffect(() => {
    if (!activeJob || !siteId) return;
    const timer = window.setInterval(async () => {
      try {
        const { job } = await api.getJob(activeJob);
        setAssistantLog((log) => log.map((entry) => entry.id === activeJob ? {
          ...entry,
          status: job.status,
          text: job.status === "ready"
            ? "Changes are ready on the canvas."
            : ["failed", "runner_start_failed", "timeout", "cancelled", "ready_unverified"].includes(job.status)
              ? `That change needs attention · ${job.status.replace(/_/g, " ")}`
              // Never surface the raw "queued" — it reads as "waiting behind other
              // people's work." Say what is actually happening to their change.
              : job.status === "building"
                ? "Building your change…"
                : job.status === "validating"
                  ? "Checking your change…"
                  : "Working on your change…",
        } : entry));
        if (job.status === "ready" || job.status === "ready_unverified") {
          window.clearInterval(timer);
          const [siteRow, buildHistory] = await Promise.all([api.getSite(siteId), api.listBuilds(siteId)]);
          setSite(siteRow);
          setBuilds(buildHistory.jobs);
          setBuildId(buildHistory.previewBuildId ?? siteRow.previewBuildId ?? siteRow.liveBuildId);
          setActiveJob(null);
          setBusy(null);
          setReady(false);
          setFrameTimedOut(false);
          setManifest(null);
          prepareAttempted.current = false;
          setFrameKey((value) => value + 1);
          if (job.status === "ready_unverified") {
            setNotice("Changes are uploaded and editable; public preview verification is still pending.");
          }
        } else if (["failed", "runner_start_failed", "timeout", "cancelled"].includes(job.status)) {
          window.clearInterval(timer);
          setActiveJob(null);
          setBusy(null);
        }
      } catch (reason: any) {
        window.clearInterval(timer);
        setError(friendlyError(reason, "We lost track of that change while it was building. Check History to see where it landed."));
        setActiveJob(null);
        setBusy(null);
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [activeJob, siteId]);

  const sendHandshake = () => {
    if (!previewOrigin) return;
    if (handshakeTimer.current) window.clearTimeout(handshakeTimer.current);
    const handshake = { type: "handshake", requestId: `${buildId}:${frameKey}:${currentPage}`, page: currentPage, mode: mode === "assistant" ? "ai" : "tactile" };
    expectedHandshake.current = handshake.requestId;
    postToPreview(frameRef.current, previewOrigin, handshake);
    handshakeTimer.current = window.setTimeout(() => postToPreview(frameRef.current, previewOrigin, handshake), 250);
  };

  useEffect(() => {
    if (ready || !previewOrigin) return;
    const timer = window.setInterval(sendHandshake, 750);
    return () => window.clearInterval(timer);
  }, [buildId, currentPage, frameKey, mode, previewOrigin, ready]);

  const selectField = (path: string) => {
    setSelectedPath(path);
    const field = model.fields.find((candidate) => candidate.path === path);
    if (field?.page && !samePage(field.page, currentPage)) {
      setCurrentPage(pageFile(field.page));
      setReady(false);
    }
    setInspectorTab("inspect");
    setRailTab("objects");
    postToPreview(frameRef.current, previewOrigin, { type: "set-selection", paths: [path] });
    if (field?.objectId) postToPreview(frameRef.current, previewOrigin, { type: "focus-object", objectId: field.objectId });
  };

  const changeField = (path: string, value: string) => {
    setValues((current) => ({ ...current, [path]: value }));
    changedFields.current.add(path);
    setDirty(true);
    const field = model.fields.find((candidate) => candidate.path === path);
    if (field?.type === "image") {
      setImageAssetIds((current) => {
        const next = { ...current };
        delete next[path];
        return next;
      });
    }
    const capability = field?.type === "image" ? "src" : "text";
    postToPreview(frameRef.current, previewOrigin, { type: "set-field", path, value, capability });
  };

  const uploadImage = async (path: string, file: File) => {
    if (!siteId || !/^image\//.test(file.type)) {
      setImageUpload({ status: "error", progress: null, file, message: "Choose a JPEG, PNG, WebP, or GIF image." });
      return;
    }
    uploadAbort.current?.abort();
    const controller = new AbortController();
    uploadAbort.current = controller;
    setImageUpload({ status: "uploading", progress: 0, file });
    try {
      const uploaded = await api.uploadSiteImage(siteId, file, {
        signal: controller.signal,
        onProgress: (progress) => setImageUpload((state) => state?.status === "uploading" ? { ...state, progress } : state),
      });
      // The old image remains untouched until the private ingest is complete.
      setImageAssetIds((current) => ({ ...current, [path]: uploaded.assetId }));
      setValues((current) => ({ ...current, [path]: uploaded.previewUrl }));
      changedFields.current.add(path);
      setDirty(true);
      postToPreview(frameRef.current, previewOrigin, {
        type: "set-field", path, value: uploaded.previewUrl, capability: "src",
      });
      setImageUpload(null);
      setNotice("Image added to the canvas. Save to make it part of your site.");
    } catch (reason: any) {
      if (reason?.name === "AbortError") {
        setImageUpload(null);
      } else {
        setImageUpload({
          status: "error", progress: null, file,
          message: reason?.payload?.error || reason?.message || "Upload failed",
        });
      }
    }
  };

  const changeAttribute = (path: string, key: "href" | "alt" | "focalX" | "focalY", value: string) => {
    const parsedValue = key === "focalX" || key === "focalY" ? Number(value) : value;
    setAttributes((current) => ({ ...current, [path]: { ...current[path], [key]: parsedValue } }));
    changedFields.current.add(path);
    setDirty(true);
    postToPreview(frameRef.current, previewOrigin, { type: "set-field", path, value, capability: key });
  };

  const setStyle = (token: string, value: string, remember = true) => {
    setStyles((current) => {
      styleUndo.current.push(current);
      styleUndo.current = styleUndo.current.slice(-30);
      styleRedo.current = [];
      return { ...current, [token]: value };
    });
    if (remember) setRecentStyles((current) => [token, ...current.filter((item) => item !== token)].slice(0, 5));
    changedStyles.current.add(token);
    setDirty(true);
    previewStyle(token, value);
  };

  const previewStyle = (token: string, value: string) => {
    if (manifest?.schemaVersion === 3) {
      const font = manifest.styles.fonts.find(item => `font.${item.id}` === token);
      if (font) postToPreview(frameRef.current, previewOrigin, { type: "set-font", ...font, family: value, cssFamily: value });
      else if (manifest.styles.tokens.some(item => item.name === token)) postToPreview(frameRef.current, previewOrigin, { type: "set-token", name: token, value });
      return;
    }
    if (PALETTE.includes(token as typeof PALETTE[number])) {
      postToPreview(frameRef.current, previewOrigin, { type: "set-color", token, value });
      return;
    }
    if (token === "motion") {
      postToPreview(frameRef.current, previewOrigin, { type: "preview-motion", preset: value, sectionId: selectedField?.sectionId });
      return;
    }
    if (token === "font.pairing") {
      const pairings: Record<string, [string, string]> = {
        modern: ["Inter, system-ui, sans-serif", "Inter, system-ui, sans-serif"],
        editorial: ["Georgia, serif", "system-ui, sans-serif"],
        classic: ["Garamond, Georgia, serif", "Arial, sans-serif"],
        friendly: ["Trebuchet MS, system-ui, sans-serif", "Verdana, sans-serif"],
        technical: ["ui-monospace, monospace", "system-ui, sans-serif"],
      };
      const [fontHeading, fontBody] = pairings[value] ?? pairings.modern;
      postToPreview(frameRef.current, previewOrigin, { type: "preview-style", tokens: { fontHeading, fontBody } });
      return;
    }
    if (token === "type.scale") {
      postToPreview(frameRef.current, previewOrigin, { type: "preview-style", tokens: { typeScale: { compact: ".9", balanced: "1", dramatic: "1.2" }[value] } });
      return;
    }
    if (token === "corners") {
      const radii: Record<string, [string, string, string]> = { sharp: ["0px", "0px", "0px"], soft: ["4px", "8px", "12px"], rounded: ["8px", "16px", "24px"], pill: ["999px", "999px", "999px"] };
      const [radiusSm, radiusMd, radiusLg] = radii[value] ?? radii.rounded;
      postToPreview(frameRef.current, previewOrigin, { type: "preview-style", tokens: { radiusSm, radiusMd, radiusLg } });
      return;
    }
    const tokenMap: Record<string, string> = {
      "motion.duration": "duration",
      "motion.easing": "easing",
      "motion.stagger": "stagger",
      spacing: "density",
    };
    if (tokenMap[token]) {
      const normalized = token === "motion.duration" || token === "motion.stagger" ? `${value}ms`
        : token === "motion.easing" && value === "spring" ? "cubic-bezier(0.34, 1.56, 0.64, 1)"
          : token === "spacing" ? ({ compact: ".85", comfortable: "1", spacious: "1.2" }[value] ?? "1") : value;
      postToPreview(frameRef.current, previewOrigin, { type: "preview-style", tokens: { [tokenMap[token]]: normalized } });
      return;
    }
    postToPreview(frameRef.current, previewOrigin, { type: "preview-style", presets: { [token]: value } });
  };

  const applyStyleSet = (next: Record<string, string>) => {
    setStyles((current) => {
      styleUndo.current.push(current);
      styleRedo.current = [];
      return { ...current, ...next };
    });
    Object.entries(next).forEach(([token, value]) => previewStyle(token, value));
    Object.keys(next).forEach((token) => changedStyles.current.add(token));
    setDirty(true);
  };

  const undoStyle = () => {
    const previous = styleUndo.current.pop();
    if (!previous) return;
    styleRedo.current.push(styles);
    setStyles(previous);
    Object.entries(previous).forEach(([token, value]) => previewStyle(token, value));
    setDirty(true);
  };

  const redoStyle = () => {
    const next = styleRedo.current.pop();
    if (!next) return;
    styleUndo.current.push(styles);
    setStyles(next);
    Object.entries(next).forEach(([token, value]) => previewStyle(token, value));
    setDirty(true);
  };

  const buildContentDocument = useCallback((): SiteVersionContent => {
    if (manifest?.schemaVersion === 3) {
      return buildSourceEditDocument({ manifest, buildId, head, values, attributes, imageAssetIds, styles, ...(preparedSource?.previewBuildId === buildId ? { sourceBuildId: preparedSource.sourceBuildId, sourceBundleHash: preparedSource.sourceBundleHash } : {}),
        changedFields: changedFields.current, changedStyles: changedStyles.current });
    }
    if (manifest?.schemaVersion === 2) {
      const operations: SiteVersionOperation[] = [];
      for (const path of changedFields.current) {
        const field = model.fields.find((candidate) => candidate.path === path);
        if (!field) continue;
        if (field.type === "link" || field.type === "url") {
          operations.push({ op: "set-link", path, href: attributes[path]?.href ?? field.values?.href ?? field.metadata?.href ?? "#", text: values[path] ?? field.value });
        } else if (field.type === "image") {
          const assetId = imageAssetIds[path];
          operations.push({
            op: "set-image",
            path,
            ...(assetId ? { assetId } : { src: imageEditSource(values[path] ?? field.values?.src ?? field.value, field.page) }),
            ...(attributes[path]?.alt !== undefined ? { alt: attributes[path].alt } : {}),
            ...(attributes[path]?.focalX !== undefined ? { focalX: attributes[path].focalX } : {}),
            ...(attributes[path]?.focalY !== undefined ? { focalY: attributes[path].focalY } : {}),
          });
        } else {
          operations.push({ op: "set-field", path, value: values[path] ?? field.value });
        }
      }
      for (const token of PALETTE) {
        if (changedStyles.current.has(token) && /^#[0-9a-f]{6}$/i.test(styles[token])) {
          operations.push({ op: "set-color", token, value: styles[token] });
        }
      }
      if (changedStyles.current.has("font.pairing") || changedStyles.current.has("type.scale")) {
        operations.push({
          op: "set-typography",
          ...(changedStyles.current.has("font.pairing") ? { fontPairing: styles["font.pairing"] as "modern" | "editorial" | "classic" | "friendly" | "technical" } : {}),
          ...(changedStyles.current.has("type.scale") ? { typeScale: styles["type.scale"] as "compact" | "balanced" | "dramatic" } : {}),
        });
      }
      if (changedStyles.current.has("spacing")) operations.push({ op: "set-spacing", density: styles.spacing as "compact" | "comfortable" | "spacious" });
      for (const [token, target] of [["corners", "radius"], ["surface", "surface"], ["buttons", "button"], ["imagery", "imagery"]] as const) {
        if (changedStyles.current.has(token)) operations.push({ op: "set-preset", target, value: styles[token] });
      }
      if (["motion", "motion.duration", "motion.easing", "motion.stagger"].some((token) => changedStyles.current.has(token))) {
        const level = styles.motion as "none" | "subtle" | "expressive";
        operations.push({
          op: "set-motion",
          level,
          durationMs: level === "none" ? 0 : Number(styles["motion.duration"]),
          easing: styles["motion.easing"] as "linear" | "ease" | "ease-in" | "ease-out" | "ease-in-out" | "spring",
          staggerMs: level === "none" ? 0 : Number(styles["motion.stagger"]),
        });
      }
      for (const [objectId, edit] of Object.entries(sectionEdits)) {
        operations.push({ op: "set-section", objectId, ...edit });
      }
      operations.push(...deletedElements);
      return { schemaVersion: 2, fields: {}, operations };
    }
    const fields = { ...(head?.content.fields ?? {}) };
    for (const [path, value] of Object.entries(values)) {
      const rawType = model.fields.find((field) => field.path === path)?.type ?? fields[path]?.type ?? "text";
      const type = rawType === "link" ? "url" : rawType;
      fields[path] = { type, value };
    }
    for (const token of PALETTE) {
      const apiToken = token === "bg" ? "background" : token === "fg" ? "text" : token;
      if (/^#[0-9a-f]{6}$/i.test(styles[token])) fields[`theme.${apiToken}`] = { type: "color", value: styles[token] };
    }
    for (const [token, value] of Object.entries(styles)) {
      if (!PALETTE.includes(token as typeof PALETTE[number])) fields[`style.${token}`] = { type: "text", value };
    }
    return { schemaVersion: 1, fields };
  }, [attributes, buildId, deletedElements, head, imageAssetIds, manifest, model.fields, preparedSource, sectionEdits, styles, values]);

  // What the loaded bundle can actually express. The tactile controls rewrite
  // --pf-* custom properties, so a stylesheet that never reads them cannot be
  // restyled this way — the panels say so instead of pretending.
  const styleCapabilities = useMemo(() => {
    const declared = manifest?.schemaVersion === 2 ? manifest.styles?.capabilities : undefined;
    return {
      authored: manifest?.schemaVersion === 3,
      defaults: authoredStyles(manifest),
      fontRoles: manifest?.schemaVersion === 3 ? manifest.styles.fonts : [],
      colors: manifest?.schemaVersion === 3 ? manifest.styles.capabilities.colors : declared?.colors ?? [],
      fonts: manifest?.schemaVersion === 3 ? manifest.styles.capabilities.fonts : !!declared?.fonts,
      typeScale: !!declared?.typeScale,
      spacing: !!declared?.spacing,
      corners: !!declared?.corners,
      surfaces: !!declared?.surfaces,
      buttons: !!declared?.buttons,
      imagery: !!declared?.imagery,
      motion: !!declared?.motion,
      known: !!manifest,
    };
  }, [manifest]);

  const contrast = manifest?.schemaVersion === 3 ? null : contrastRatio(styles.fg, styles.bg);
  const contrastError = contrast !== null && contrast < 4.5 ? `Body text contrast is ${contrast.toFixed(1)}:1. Use at least 4.5:1.` : null;

  const save = useCallback(async (): Promise<boolean> => {
    if (!siteId || !head || contrastError) return false;
    const contentDocument = buildContentDocument();
    if (contentDocument.schemaVersion === 2 && (contentDocument.operations?.length ?? 0) === 0) {
      setDirty(false);
      setNotice("Everything was already saved.");
      return true;
    }
    setBusy("save");
    setSaveProgress("Saving draft…");
    setError(null);
    setConflict(null);
    let savedVersion: number | null = null;
    try {
      const saved = await api.saveSiteContent(siteId, contentDocument, head.version, "Visual Editor");
      savedVersion = saved.version;
      // Save and preview are distinct durable steps. If delivery is interrupted,
      // the next Save must reconnect to this saved version instead of colliding
      // with the already advanced head.
      setHead(await api.getSiteContent(siteId));
      setSaveProgress("Starting preview build…");
      const result = await api.applySiteContent(siteId, saved.version);
      // `applied` means the API shipped the bundle in-process — the build is
      // already there, so polling would only add latency to a change that has
      // finished. Older deployments answer without the flag and still poll.
      const terminal = result.applied ? "ready" : await waitForAppliedBuild(result.jobId, (status) =>
        setSaveProgress(`Preview · ${status.replace(/_/g, " ")}…`));
      const [nextHead, nextSite] = await Promise.all([api.getSiteContent(siteId), api.getSite(siteId)]);
      setHead(nextHead);
      setSite(nextSite);
      setBuildId(result.jobId);
      setManifest(null);
      prepareAttempted.current = false;
      setReady(false);
      setDirty(false);
      changedFields.current.clear();
      changedStyles.current.clear();
      setSectionEdits({});
      setDeletedElements([]);
      setFrameKey((value) => value + 1);
      setNotice(terminal === "ready_unverified"
        ? `Version ${saved.version} was saved, but public preview verification needs attention.`
        : saved.unchanged ? "Everything was already saved." : "Draft saved. Review your preview, then publish when you’re ready.");
      // Re-enable the editor now; the change is persisted. The history list
      // refreshes in the background rather than holding "saved" for a round-trip.
      setBusy(null);
      void refreshVersions();
      return true;
    } catch (reason: any) {
      if (savedVersion !== null) {
        setError(savedPreviewFailure(savedVersion));
        void refreshVersions();
      } else if (isVersionConflict(reason)) {
        try {
          const server = await api.getSiteContent(siteId);
          setConflict({ local: contentDocument, server });
          setError("This site changed elsewhere. Your edits are preserved; choose which version to continue from.");
        } catch {
          setError("This site changed elsewhere. Your local edits are preserved; reload when the server is available.");
        }
      } else {
        setError(friendlyError(reason, "Your changes could not be saved. They are still here — try Save again."));
      }
      return false;
    } finally {
      setBusy(null);
      setSaveProgress(null);
    }
  }, [buildContentDocument, contrastError, head, refreshVersions, siteId]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty) event.preventDefault();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const modifier = event.ctrlKey || event.metaKey;
      if (modifier && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (dirty && !busy && !contrastError) void save();
      }
      if (modifier && event.key.toLowerCase() === "z" && inspectorTab === "styles") {
        event.preventDefault();
        event.shiftKey ? redoStyle() : undoStyle();
      }
      if (event.key === "Escape" && selectedPath) {
        setSelectedPath(null);
        postToPreview(frameRef.current, previewOrigin, { type: "clear-selection" });
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [busy, contrastError, dirty, inspectorTab, previewOrigin, save, selectedPath]);

  // Default to the site's own preview pointer, not whatever the canvas is
  // showing. The API only promotes a build that equals previewBuildId, so
  // publishing "what I'm looking at" 409s the moment History previews an
  // older version. The History panel still passes an explicit build id.
  async function publish(targetBuildId = site?.previewBuildId ?? buildId) {
    if (!siteId || !targetBuildId) return;
    setBusy("publish");
    setError(null);
    try {
      const updated = await api.publish(siteId, targetBuildId);
      setSite(updated);
      setNotice("Published. Your latest draft is live; updates may take a few seconds to appear.");
    } catch (reason: any) {
      setError(friendlyError(reason, "That version could not be published just yet."));
    } finally {
      setBusy(null);
    }
  }

  async function askAssistant() {
    // requestNewPage has always guarded on busy/activeJob; this path did not,
    // so Enter could fire a second paid build over a running one — and the
    // winner's bundle orphaned the loser's.
    if (!ASSISTANT_ENABLED || !siteId || !assistantMessage.trim() || busy || activeJob) return;

    // Unsaved direct edits build from the last SAVED version. Rather than
    // interrupt every message with a modal, fold them in first — the inline
    // apply costs seconds. Only when Save genuinely cannot run (a contrast error
    // blocks it) do we fall back to the old choice.
    if (dirty) {
      if (contrastError) {
        if (!(await dialog.confirm({
          title: "Fix the contrast warning to include your edits",
          body: "Your unsaved edits include a contrast issue that blocks saving, and ProtoPal builds from the last saved version. Continue without those edits, or go back and fix the contrast first.",
          confirmLabel: "Continue without them",
          cancelLabel: "Keep editing",
        }))) return;
      } else if (!(await save())) {
        // save() surfaced its own error and kept the edits — do not dispatch.
        return;
      }
    }

    const message = assistantMessage.trim();
    setBusy("assistant");
    setError(null);
    setAssistantMessage("");
    setAssistantLog((log) => [...log, { id: `user-${Date.now()}`, role: "user", text: message }]);

    // Fast path. Most editor requests are "change this text" — those are field
    // operations, resolvable in a couple of seconds without a container or an
    // agent session. Anything structural comes back `escalated` and falls
    // through to the edit engine below.
    //
    // If the resolver asked something last turn, this message is the answer —
    // send both halves so "the top one" still means something.
    const pending = pendingQuestion.current;
    try {
      const quick = await api.quickEdit(siteId, {
        instruction: message,
        page: currentPageModel?.route ?? currentPage,
        selectedFieldPath: selectedField?.path,
        priorInstruction: pending?.instruction,
        priorQuestion: pending?.question,
      });
      if (!quick.escalated && quick.question) {
        pendingQuestion.current = { instruction: pending?.instruction ?? message, question: quick.question };
        setAssistantLog((log) => [...log, {
          id: `ask-${Date.now()}`, role: "assistant", text: quick.question!, status: "ready",
        }]);
        setBusy(null);
        return;
      }
      if (!quick.escalated && quick.ops?.length) {
        pendingQuestion.current = null;
        // Show it on the canvas first, then persist through the ordinary save
        // path so versioning, conflicts and history keep one implementation.
        for (const op of quick.ops) {
          setValues((current) => ({ ...current, [op.path]: op.value }));
          changedFields.current.add(op.path);
          postToPreview(frameRef.current, previewOrigin, { type: "set-field", path: op.path, value: op.value });
        }
        setDirty(true);
        setAssistantLog((log) => [...log, {
          id: `quick-${Date.now()}`, role: "assistant",
          text: quick.summary || "Updated the text you asked about.", status: "ready",
        }]);
        setBusy(null);
        return;
      }
      // Escalated (or an empty reply): fall through to the edit engine below.
    } catch {
      // A resolver problem must never lose the request — fall through to the
      // engine, which is slower but can do everything.
    }

    // Hand to the edit engine. If the owner was answering a resolver question,
    // give the engine the whole exchange — the bare answer ("the top one") is
    // meaningless alone — then clear the pending question so an unrelated next
    // message can never ride along as an answer. This clear also covers the case
    // where quickEdit threw before reaching the clear above (the stale-question
    // contamination bug).
    const engineMessage = pending
      ? `${pending.instruction}\n\nWhen asked "${pending.question}", the owner answered: ${message}`
      : message;
    pendingQuestion.current = null;

    try {
      const { jobId } = await api.startBuild(siteId, "iterate", engineMessage, {
        manifestVersion: manifest?.schemaVersion ?? 1,
        ...(preparedSource?.previewBuildId === buildId && preparedSource.sourceBundleHash ? { sourceBundleHash: preparedSource.sourceBundleHash } : {}),
        page: currentPageModel?.route ?? currentPage,
        mode: "assistant",
        selectedObjectId: selectedObject?.id,
        selectedFieldPath: selectedField?.path,
        visibleFieldPaths: model.fields.filter((field) => field.page === currentPage).map((field) => field.path),
      }, selectedField || selectedObject ? {
        path: selectedField?.path ?? selectedObject?.id,
        fieldPath: selectedField?.path,
        objectId: selectedObject?.id,
        page: currentPageModel?.route ?? currentPage,
        kind: selectedField?.type === "url" ? "link" : selectedField?.type ?? (selectedObject?.kind === "section" ? "section" : undefined),
        label: selectedField?.label ?? selectedObject?.label,
        value: selectedField ? values[selectedField.path] : undefined,
        tag: selectedField?.tag as "h1" | "h2" | "h3" | "h4" | "p" | "li" | "a" | "button" | "img" | "section" | undefined,
      } : undefined);
      setActiveJob(jobId);
      setAssistantLog((log) => [...log, { id: jobId, role: "assistant", text: "Starting your change…", status: "queued" }]);
    } catch (reason: any) {
      // Don't strand the owner: put their words back in the composer so one tap
      // resends instead of retyping.
      setAssistantMessage(message);
      setError(friendlyError(reason, "ProtoPal could not start that change. Try again in a moment."));
      setBusy(null);
    }
  }

  async function requestNewPage(name: string, purpose: string) {
    // A new page is authored by the assistant, so it rides the same engine.
    if (!ASSISTANT_ENABLED || !siteId || !name.trim() || busy || activeJob) return;
    const pageName = name.trim();
    // The engine's omit-when-silent rules own content truth; this message only
    // scopes the structural ask — never inject business facts from the client.
    const message =
      `Add a new page to this site named "${pageName}".` +
      (purpose.trim() ? ` The owner describes it as: ${purpose.trim()}.` : "") +
      " Build it from what is already known about this business (omit anything unknown), link it in the site navigation on every page, and keep the existing design system.";
    setMode("assistant");
    setBusy("assistant");
    setError(null);
    setAssistantLog((log) => [...log, { id: `user-${Date.now()}`, role: "user", text: `Add a page: ${pageName}` }]);
    try {
      const { jobId } = await api.startBuild(siteId, "iterate", message, {
        manifestVersion: manifest?.schemaVersion ?? 1,
        ...(preparedSource?.previewBuildId === buildId && preparedSource.sourceBundleHash ? { sourceBundleHash: preparedSource.sourceBundleHash } : {}),
        page: currentPageModel?.route ?? currentPage,
        mode: "assistant",
      });
      setActiveJob(jobId);
      setAssistantLog((log) => [...log, { id: jobId, role: "assistant", text: `Building the ${pageName} page…`, status: "queued" }]);
    } catch (reason: any) {
      setError(friendlyError(reason, `The ${pageName} page could not be started. Try again in a moment.`));
      setBusy(null);
    }
  }

  async function revert(version: number) {
    if (!siteId || !head) return;
    const confirmed = await dialog.confirm({
      title: `Restore version ${version}?`,
      body: `${dirty ? "Your unsaved edits will be replaced. " : ""}It comes back as a new draft. Your live site will not change until you publish.`,
      confirmLabel: "Restore as draft",
    });
    if (!confirmed) return;
    setBusy("revert");
    setSaveProgress("Creating restore draft…");
    setError(null);
    let restoredVersion: number | null = null;
    try {
      const restored = await api.revertSiteVersion(siteId, version, head.version);
      restoredVersion = restored.version;
      setSaveProgress("Building restore preview…");
      const applied = await api.applySiteContent(siteId, restored.version);
      const terminal = applied.applied ? "ready" : await waitForAppliedBuild(applied.jobId, (status) =>
        setSaveProgress(`Restore preview · ${status.replace(/_/g, " ")}…`));
      const [nextHead, nextSite] = await Promise.all([api.getSiteContent(siteId), api.getSite(siteId)]);
      setHead(nextHead);
      setSite(nextSite);
      setValues(Object.fromEntries(Object.entries(nextHead.content.fields).filter(([, field]) => field.type !== "color").map(([key, field]) => [key, field.value])));
      setBuildId(applied.jobId);
      setManifest(null);
      prepareAttempted.current = false;
      setDirty(false);
      changedFields.current.clear();
      changedStyles.current.clear();
      setSectionEdits({});
      setDeletedElements([]);
      setNotice(terminal === "ready_unverified"
        ? `Draft v${restored.version} was restored, but preview verification needs attention. Live is unchanged.`
        : `Version ${version} restored as draft v${restored.version}. Live is unchanged until you publish.`);
      await refreshVersions();
    } catch (reason: any) {
      if (isVersionConflict(reason) && restoredVersion === null) {
        setError("Restore conflicted with a newer edit. Your local state is preserved; refresh history and try again.");
      } else if (restoredVersion !== null) {
        setError(`Restore draft v${restoredVersion} was created, but its preview could not be built. Live is unchanged and your local state is preserved.`);
        await refreshVersions().catch(() => undefined);
      } else {
        setError(friendlyError(reason, "That version could not be restored. Your live site is unchanged."));
      }
    } finally {
      setBusy(null);
      setSaveProgress(null);
    }
  }

  async function labelVersion(version: SiteVersionSummary) {
    if (!siteId) return;
    const label = await dialog.promptText({
      title: `Name version ${version.version}`,
      label: "Version name",
      placeholder: "e.g. Before the summer menu",
      defaultValue: version.label ?? "",
      confirmLabel: "Save name",
      maxLength: 80,
    });
    if (label === null) return;
    await api.labelSiteVersion(siteId, version.version, label.trim() || null);
    await refreshVersions();
  }

  async function deleteSelectedElement() {
    if (!selectedObject || !selectedObject.operations?.includes("delete")) return;
    const target: "field" | "section" | "object" =
      selectedObject.fieldPath ? "field" : selectedObject.kind === "section" ? "section" : "object";
    const id = selectedObject.fieldPath ?? selectedObject.id;
    const confirmed = await dialog.confirm({
      title: `Delete ${selectedObject.label}?`,
      body: "You can undo this any time before you save.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    const operation = { op: "delete-element" as const, target, id };
    setDeletedElements((current) => [
      ...current.filter((item) => !(item.target === target && item.id === id)),
      operation,
    ]);
    postToPreview(frameRef.current, previewOrigin, { type: "delete-element-preview", target, id });
    setSelectedPath(null);
    setDirty(true);
    setNotice(`${selectedObject.label} deleted. Use Undo before saving to restore it.`);
  }

  function undoDelete() {
    const operation = deletedElements[deletedElements.length - 1];
    if (!operation) return;
    setDeletedElements((current) => current.slice(0, -1));
    postToPreview(frameRef.current, previewOrigin, {
      type: "restore-element-preview",
      target: operation.target,
      id: operation.id,
    });
    setDirty(changedFields.current.size > 0 || changedStyles.current.size > 0 ||
      Object.keys(sectionEdits).length > 0 || deletedElements.length > 1);
    setNotice("Deletion undone.");
  }

  const runOperation = (operation: string) => {
    if (operation === "delete") {
      void deleteSelectedElement();
      return;
    }
    if (!selectedObject || selectedObject.kind !== "section") return;
    const sections = model.pages.find((page) => page.path === selectedObject.page)?.sections ?? [];
    const currentIndex = Math.max(0, sections.findIndex((section) => section.id === selectedObject.id));
    const edit = operation === "hide" ? { visible: false }
      : operation === "show" ? { visible: true }
        : operation === "move-up" ? { order: Math.max(0, currentIndex - 1) }
          : operation === "move-down" ? { order: Math.min(sections.length - 1, currentIndex + 1) }
            : null;
    if (!edit) return;
    setSectionEdits((current) => ({ ...current, [selectedObject.id]: { ...current[selectedObject.id], ...edit } }));
    postToPreview(frameRef.current, previewOrigin, { type: "section-operation", sectionId: selectedObject.id, operation, ...edit });
    setDirty(true);
    setNotice(`${operation.replace("-", " ")} applied. Save to keep it.`);
  };

  const resolveConflict = async (keepLocal: boolean) => {
    if (!conflict) return;
    const resolution = ++conflictResolution.current;
    if (!keepLocal) {
      // A saved v2 operation snapshot can be newer than the rendered bundle.
      // Do not discard local edits and then present that older bundle as latest.
      let server: SiteContentHead;
      try { server = await api.getSiteContent(siteId!); }
      catch {
        if (resolution !== conflictResolution.current) return;
        setError("The server version could not be loaded. Your local edits are still here; try Reload server version again.");
        return;
      }
      if (resolution !== conflictResolution.current) return;
      if (hasPendingV2Preview(server)) {
        setConflict({ ...conflict, server });
        setError(`Server draft ${server.version} is saved, but its preview is not ready. Your local edits are still here. Try Reload server version after the preview finishes, or choose Keep my edits.`);
        return;
      }
      // Reopen the saved site, including its current bundle and manifest. Merely
      // clearing the text fields leaves local links, image metadata and styles
      // attached to a preview that no longer matches the chosen server version.
      reloadGeneration.current += 1;
      uploadAbort.current?.abort();
      setBusy(null);
      setImageUpload(null);
      setSite(null);
      setHead(null);
      setBuildId(null);
      setManifest(null);
      setPreparedSource(null);
      prepareAttempted.current = false;
      setReady(false);
      setFrameTimedOut(false);
      setFrameKey(value => value + 1);
      setSelectedPath(null);
      setValues({});
      setAttributes({});
      setImageAssetIds({});
      setStyles(DEFAULT_STYLES);
      styleUndo.current = [];
      styleRedo.current = [];
      setRecentStyles([]);
      changedFields.current.clear();
      changedStyles.current.clear();
      setSectionEdits({});
      setDeletedElements([]);
      setDirty(false);
      setNotice(null);
      setReloadKey(value => value + 1);
    } else {
      setHead(conflict.server);
      setDirty(true);
      setNotice("Local edits kept. Save again to apply them over the latest version.");
    }
    setConflict(null);
    setError(null);
  };


  const timeline = [
    ...versions.map((version) => ({ kind: "version" as const, at: new Date(version.createdAt).getTime(), version })),
    ...builds.map((build) => ({ kind: "build" as const, at: build.createdAt, build })),
  ].sort((a, b) => b.at - a.at);

  return {
    site, head, error, notice, conflict, dirty, busy, saveProgress,
    mode, setMode, railTab, setRailTab, inspectorTab, setInspectorTab,
    currentPage, setCurrentPage, selectedPath, setSelectedPath,
    values, attributes, imageUpload, styles, recentStyles,
    assistantMessage, setAssistantMessage, assistantLog, activeJob,
    motionPaused, setMotionPaused, advancedMotion, setAdvancedMotion,
    frameRef, frameKey, setFrameKey, buildId, setBuildId, ready, setReady, frameTimedOut, prepareAttempted, manifest,
    previewUrl, publicPreviewUrl, previewBase, previewOrigin, selectedField, selectedObject, pageObjects,
    model, timeline, contrastError, uploadAbort, styleCapabilities,
    canEditImageFocal: manifest?.schemaVersion !== 3 || !!(selectedPath && (imageAssetIds[selectedPath] || manifest.fields.find(field => field.path === selectedPath)?.metadata?.assetId)),
    prepare, sendHandshake, selectField, changeField, uploadImage, changeAttribute,
    setStyle, applyStyleSet, undoStyle, redoStyle, styleUndo, styleRedo,
    save, publish, askAssistant, requestNewPage, revert, labelVersion, runOperation, resolveConflict,
    deletedElements, undoDelete,
    setNotice, setError, pageLabel,
    pageHint, setPageHint,
    postPreview: (message: Record<string, unknown>) => postToPreview(frameRef.current, previewOrigin, message),
  };
}

export type SiteEditorState = ReturnType<typeof useSiteEditor>;
